import { firstValue, latestValue } from '../../utils'

import type { SummaryMetricItem } from './SummaryMetrics'

type FollowersPoint = { value?: number | null }

/**
 * Copy for the followers card's info popover. Spells out that the value is a
 * point-in-time count (not a sum over the range) and that some networks only
 * report followers periodically — so a channel with monthly snapshots shows
 * the most recent available reading rather than a value dated to the exact
 * end of the range. See CORE-6157.
 */
export const FOLLOWERS_METRIC_DESCRIPTION =
  'Your follower count at the end of the selected period. Some networks only report followers periodically, so this reflects the most recent available reading.'

/**
 * Builds the "Total Followers" tile shown first in the single-channel Summary.
 *
 * Followers is a point-in-time metric, so the value is the channel's most
 * recent reading in the selected period (mirrors the All-Channels view, which
 * ranks channels by their current follower count). The delta baseline is:
 *
 *   - the comparison window's ending count when a comparison is active — so the
 *     tile reads against the same "Compared to …" period as the other cards; or
 *   - the first reading in the selected period otherwise — so the tile still
 *     shows growth over the range on its own.
 *
 * The delta is absolute (e.g. "+42") rather than a percentage: an exact
 * follower gain matches the user's mental model better than a percentage
 * against an unremembered base (see CORE-5624).
 *
 * Returns `null` when the channel has no follower readings at all (e.g. Google
 * Business Profile, or a period with no snapshot yet), so the caller can omit
 * the tile entirely rather than render an empty one.
 */
export function buildFollowersSummaryMetric({
  primary,
  comparison,
}: {
  primary: ReadonlyArray<FollowersPoint>
  comparison?: ReadonlyArray<FollowersPoint>
}): SummaryMetricItem | null {
  const current = latestValue(primary)
  if (current == null) return null

  const baseline =
    (comparison ? latestValue(comparison) : null) ?? firstValue(primary)

  const difference =
    baseline != null && baseline !== current ? current - baseline : null

  return {
    type: 'followers',
    // Integer count, like reactions/comments/reach — only `percentage` changes
    // how the value renders.
    unit: 'count',
    // "Total" disambiguates a point-in-time count from the surrounding
    // period-scoped tiles, which otherwise read as gained-this-period.
    name: 'Total Followers',
    description: FOLLOWERS_METRIC_DESCRIPTION,
    value: current,
    difference,
    differenceMode: 'absolute',
  }
}
